import { gql } from '@apollo/client';
import axios from 'axios';
import { apolloClient } from '../apollo/client';
import { dispatchGlobalError } from '../context/ApiErrorContext';
import { dispatchLogout } from '../context/AuthContext';
import type { Liga, LigaInput, LigaConnection, LigaFiltros, IuguConfig } from '../types/liga';

// Fragment reutilizável para Liga
export const LIGA_FRAGMENT = gql`
  fragment LigaFragment on Liga {
    id
    nome
    username
    imagemUrl
    bannerUrl
    enderecoCompleto
    ramoAtividade
    totalTorneios
    totalJogadores
    criadoEm
    atualizadoEm
    iuguConfig {
      id
      ambiente
      ativo
      accountId
      webhookUrl
      enableCreditCard
      enablePix
      enableBankSlip
      invoicePrefix
      defaultDueDays
      lateFinePercent
      monthlyInterestPercent
      notificationUrl
      apiTokenMasked
      webhookSecretMasked
      criadoEm
      atualizadoEm
    }
  }
`;

// Re-export types for convenience
export type { Liga, LigaInput, LigaConnection, LigaFiltros, IuguConfig } from '../types/liga';

// File upload types
export interface UploadResponse {
  url: string;
}

// Queries
export const LIGAS = gql`
  ${LIGA_FRAGMENT}
  query Ligas {
    ligas {
      ...LigaFragment
    }
  }
`;

export const LIGAS_POR_NOME = gql`
  ${LIGA_FRAGMENT}
  query LigasPorNome(
    $nome: String!
    $page: Int!
    $size: Int!
    $sortBy: String!
    $direction: String!
  ) {
    ligasPorNome(
      nome: $nome
      page: $page
      size: $size
      sortBy: $sortBy
      direction: $direction
    ) {
      page
      size
      totalItems
      totalPages
      items {
        ...LigaFragment
      }
    }
  }
`;

export const LIGAS_FILTRADAS = gql`
  ${LIGA_FRAGMENT}
  query LigasFiltradas(
    $nome: String
    $username: String
    $iuguConfigId: ID
    $page: Int!
    $size: Int!
    $sortBy: String!
    $direction: String!
  ) {
    ligasFiltradas(
      nome: $nome
      username: $username
      iuguConfigId: $iuguConfigId
      page: $page
      size: $size
      sortBy: $sortBy
      direction: $direction
    ) {
      page
      size
      totalItems
      totalPages
      items {
        ...LigaFragment
      }
    }
  }
`;

export const LIGA_POR_ID = gql`
  ${LIGA_FRAGMENT}
  query LigaPorId($id: ID!) {
    ligaPorId(id: $id) {
      ...LigaFragment
    }
  }
`;

export const LIGA_POR_USERNAME = gql`
  ${LIGA_FRAGMENT}
  query LigaPorUsername($username: String!) {
    ligaPorUsername(username: $username) {
      ...LigaFragment
    }
  }
`;

export const JOGADORES_DA_LIGA = gql`
  query JogadoresDaLiga($ligaId: ID!) {
    jogadoresDaLiga(ligaId: $ligaId) {
      id
      cpf
      telefone
      genero
      dataNascimento
      usuario {
        id
        nome
        email
        imagemUrl
        username
      }
    }
  }
`;

export const SEGUIDORES_DA_LIGA = gql`
  query SeguidoresDaLiga($ligaId: ID!) {
    seguidoresDaLiga(ligaId: $ligaId) {
      id
      cpf
      telefone
      genero
      dataNascimento
      usuario {
        id
        nome
        email
        imagemUrl
        username
      }
    }
  }
`;

// Mutations
export const CRIAR_LIGA = gql`
  ${LIGA_FRAGMENT}
  mutation CriarLiga($input: LigaInput!) {
    criarLiga(input: $input) {
      ...LigaFragment
    }
  }
`;

export const ATUALIZAR_LIGA = gql`
  ${LIGA_FRAGMENT}
  mutation AtualizarLiga($id: ID!, $input: LigaInput!) {
    atualizarLiga(id: $id, input: $input) {
      ...LigaFragment
    }
  }
`;

export const DELETAR_LIGA = gql`
  mutation DeletarLiga($id: ID!) {
    deletarLiga(id: $id)
  }
`;

export const ATUALIZAR_IMAGEM_LIGA = gql`
  ${LIGA_FRAGMENT}
  mutation AtualizarImagemLiga($id: ID!, $imagemUrl: String!) {
    atualizarImagemLiga(id: $id, imagemUrl: $imagemUrl) {
      ...LigaFragment
    }
  }
`;

export const ATUALIZAR_BANNER_LIGA = gql`
  ${LIGA_FRAGMENT}
  mutation AtualizarBannerLiga($id: ID!, $bannerUrl: String!) {
    atualizarBannerLiga(id: $id, bannerUrl: $bannerUrl) {
      ...LigaFragment
    }
  }
`;

// Interfaces para variáveis das queries/mutations
export interface LigasData {
  ligas: Liga[];
}

export interface LigasPorNomeData {
  ligasPorNome: LigaConnection;
}

export interface LigasPorNomeVars {
  nome: string;
  page: number;
  size: number;
  sortBy: string;
  direction: string;
}

export interface LigasFiltradasData {
  ligasFiltradas: LigaConnection;
}

export interface LigasFiltradasVars {
  nome?: string | null;
  username?: string | null;
  iuguConfigId?: string | null;
  page: number;
  size: number;
  sortBy: string;
  direction: string;
}

export interface LigaPorIdData {
  ligaPorId: Liga;
}

export interface LigaPorIdVars {
  id: string;
}

export interface LigaPorUsernameData {
  ligaPorUsername: Liga;
}

export interface LigaPorUsernameVars {
  username: string;
}

export interface CriarLigaData {
  criarLiga: Liga;
}

export interface CriarLigaVars {
  input: LigaInput;
}

export interface AtualizarLigaData {
  atualizarLiga: Liga;
}

export interface AtualizarLigaVars {
  id: string;
  input: LigaInput;
}

export interface DeletarLigaData {
  deletarLiga: boolean;
}

export interface DeletarLigaVars {
  id: string;
}

export interface AtualizarImagemLigaData {
  atualizarImagemLiga: Liga;
}

export interface AtualizarImagemLigaVars {
  id: string;
  imagemUrl: string;
}

export interface AtualizarBannerLigaData {
  atualizarBannerLiga: Liga;
}

export interface AtualizarBannerLigaVars {
  id: string;
  bannerUrl: string;
}

export interface Atleta {
  id: string;
  cpf?: string | null;
  telefone?: string | null;
  genero?: string | null;
  dataNascimento?: string | null;
  usuario: {
    id: string;
    nome: string;
    email: string;
    imagemUrl?: string | null;
    username?: string | null;
  };
}

export interface JogadoresDaLigaData {
  jogadoresDaLiga: Atleta[];
}

export interface JogadoresDaLigaVars {
  ligaId: string;
}

export interface SeguidoresDaLigaData {
  seguidoresDaLiga: Atleta[];
}

export interface SeguidoresDaLigaVars {
  ligaId: string;
}

// Funções de serviço com tratamento de erro
const handleError = (error: any, defaultMessage: string): never => {
  if (error.graphQLErrors) {
    const graphQLError = error.graphQLErrors[0];
    if (graphQLError?.extensions?.code === 'UNAUTHENTICATED') {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
  }

  if (error.response?.status === 401) {
    dispatchLogout();
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw error;
  }

  let message = defaultMessage;
  if (error.graphQLErrors?.[0]?.message) {
    message = error.graphQLErrors[0].message;
  } else if (error.message) {
    message = error.message;
  }

  dispatchGlobalError(message);
  throw error;
};

export const buscarLigas = async (): Promise<Liga[]> => {
  try {
    const { data } = await apolloClient.query<LigasData>({
      query: LIGAS,
      fetchPolicy: 'no-cache',
    });
    return data.ligas;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar ligas. Tente novamente.');
  }
};

export const buscarLigasPorNome = async (
  nome: string,
  page: number = 0,
  size: number = 10,
  sortBy: string = 'nome',
  direction: string = 'ASC'
): Promise<LigaConnection> => {
  try {
    const { data } = await apolloClient.query<LigasPorNomeData, LigasPorNomeVars>({
      query: LIGAS_POR_NOME,
      variables: { nome, page, size, sortBy, direction },
      fetchPolicy: 'no-cache',
    });
    return data.ligasPorNome;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar ligas por nome. Tente novamente.');
  }
};

export const buscarLigasFiltradas = async (
  filtros: LigaFiltros,
  page: number = 0,
  size: number = 10,
  sortBy: string = 'nome',
  direction: string = 'ASC'
): Promise<LigaConnection> => {
  try {
    const { data } = await apolloClient.query<LigasFiltradasData, LigasFiltradasVars>({
      query: LIGAS_FILTRADAS,
      variables: {
        ...filtros,
        page,
        size,
        sortBy,
        direction,
      },
      fetchPolicy: 'no-cache',
    });
    return data.ligasFiltradas;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar ligas filtradas. Tente novamente.');
  }
};

export const buscarLigaPorId = async (id: string): Promise<Liga> => {
  try {
    const { data } = await apolloClient.query<LigaPorIdData, LigaPorIdVars>({
      query: LIGA_POR_ID,
      variables: { id },
      fetchPolicy: 'no-cache',
    });
    return data.ligaPorId;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar liga. Tente novamente.');
  }
};

export const buscarLigaPorUsername = async (username: string): Promise<Liga> => {
  try {
    const { data } = await apolloClient.query<LigaPorUsernameData, LigaPorUsernameVars>({
      query: LIGA_POR_USERNAME,
      variables: { username },
      fetchPolicy: 'no-cache',
    });
    return data.ligaPorUsername;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar liga por username. Tente novamente.');
  }
};

export const criarLiga = async (input: LigaInput): Promise<Liga> => {
  try {
    const { data } = await apolloClient.mutate<CriarLigaData, CriarLigaVars>({
      mutation: CRIAR_LIGA,
      variables: { input },
    });
    if (!data?.criarLiga) {
      throw new Error('Não foi possível criar a liga.');
    }
    return data.criarLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao criar liga. Tente novamente.');
  }
};

export const atualizarLiga = async (id: string, input: LigaInput): Promise<Liga> => {
  try {
    const { data } = await apolloClient.mutate<AtualizarLigaData, AtualizarLigaVars>({
      mutation: ATUALIZAR_LIGA,
      variables: { id, input },
    });
    if (!data?.atualizarLiga) {
      throw new Error('Não foi possível atualizar a liga.');
    }
    return data.atualizarLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao atualizar liga. Tente novamente.');
  }
};

export const deletarLiga = async (id: string): Promise<boolean> => {
  try {
    const { data } = await apolloClient.mutate<DeletarLigaData, DeletarLigaVars>({
      mutation: DELETAR_LIGA,
      variables: { id },
    });
    return data?.deletarLiga ?? false;
  } catch (error: any) {
    return handleError(error, 'Erro ao deletar liga. Tente novamente.');
  }
};

export const atualizarImagemLiga = async (id: string, imagemUrl: string): Promise<Liga> => {
  try {
    const { data } = await apolloClient.mutate<AtualizarImagemLigaData, AtualizarImagemLigaVars>({
      mutation: ATUALIZAR_IMAGEM_LIGA,
      variables: { id, imagemUrl },
    });
    if (!data?.atualizarImagemLiga) {
      throw new Error('Não foi possível atualizar a imagem da liga.');
    }
    return data.atualizarImagemLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao atualizar imagem da liga. Tente novamente.');
  }
};

export const atualizarBannerLiga = async (id: string, bannerUrl: string): Promise<Liga> => {
  try {
    const { data } = await apolloClient.mutate<AtualizarBannerLigaData, AtualizarBannerLigaVars>({
      mutation: ATUALIZAR_BANNER_LIGA,
      variables: { id, bannerUrl },
    });
    if (!data?.atualizarBannerLiga) {
      throw new Error('Não foi possível atualizar o banner da liga.');
    }
    return data.atualizarBannerLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao atualizar banner da liga. Tente novamente.');
  }
};

export const buscarJogadoresDaLiga = async (ligaId: string): Promise<Atleta[]> => {
  try {
    const { data } = await apolloClient.query<JogadoresDaLigaData, JogadoresDaLigaVars>({
      query: JOGADORES_DA_LIGA,
      variables: { ligaId },
      fetchPolicy: 'no-cache',
    });
    return data.jogadoresDaLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar jogadores da liga. Tente novamente.');
  }
};

export const buscarSeguidoresDaLiga = async (ligaId: string): Promise<Atleta[]> => {
  try {
    const { data } = await apolloClient.query<SeguidoresDaLigaData, SeguidoresDaLigaVars>({
      query: SEGUIDORES_DA_LIGA,
      variables: { ligaId },
      fetchPolicy: 'no-cache',
    });
    return data.seguidoresDaLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar seguidores da liga. Tente novamente.');
  }
};

// REST API functions for file uploads
const restUrl = (process.env.REACT_APP_GRAPHQL_ENDPOINT || '') + 'api/';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const uploadImagemLiga = async (id: string, file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${restUrl}ligas/${id}/imagem`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Liga não encontrada.');
    }
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    const message = error.response?.data?.message || 'Erro ao fazer upload da imagem.';
    dispatchGlobalError(message);
    throw new Error(message);
  }
};

export const uploadBannerLiga = async (id: string, file: File): Promise<string> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${restUrl}ligas/${id}/banner`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Liga não encontrada.');
    }
    if (error.response?.status === 401) {
      dispatchLogout();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      throw error;
    }
    const message = error.response?.data?.message || 'Erro ao fazer upload do banner.';
    dispatchGlobalError(message);
    throw new Error(message);
  }
};

// ---------------------------------------------------------------------------
// Saldo da conta da Liga
// ---------------------------------------------------------------------------

export const SALDO_CONTA_LIGA = gql`
  query SaldoContaLiga($ligaId: ID!) {
    saldoContaLiga(ligaId: $ligaId)
  }
`;

interface SaldoContaLigaData {
  saldoContaLiga: number;
}

interface SaldoContaLigaVars {
  ligaId: string;
}

export const buscarSaldoContaLiga = async (ligaId: string): Promise<number> => {
  try {
    const { data } = await apolloClient.query<SaldoContaLigaData, SaldoContaLigaVars>({
      query: SALDO_CONTA_LIGA,
      variables: { ligaId },
      fetchPolicy: 'no-cache',
    });
    return data.saldoContaLiga;
  } catch (error: any) {
    return handleError(error, 'Erro ao buscar saldo da conta da liga.');
  }
};